import { PermissionSuffix } from '../../_generated/api/user-management'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card } from 'primereact/card'

import { Row } from './pure/FlexContainer'
import PrimeIcon from './pure/PrimeIcon'
import CustomIcon from './pure/CustomIcon'
import {
    getMenuItemArchive,
    getMenuItemDuplicate,
    getMenuItemExport,
    getMenuItemRestore,
    getMenuItemViewQr,
} from './menu-items'
import { useExportContext } from '../contexts/ExportContext'
import { useStructureContext } from '../contexts/StructureContext'
import { useUserContext } from '../contexts/UserContext'
import QrDialog from '../dialogs/QrDialog'
import CardContextMenu from './CardContextMenu'
import { useQR } from '../../hooks/qr'
import DirectoryEntry from '../../models/DirectoryEntry'
import { StructureTypes } from '../../models/Structure'
import MenuBuilder from '../../tools/MenuBuilder'
import t from '../../tools/t'
import ClassNameBuilder from '../../tools/ClassNameBuilder'

interface EntryCardProps {
    entry: DirectoryEntry
}
export const EntryCard = ({ entry }: EntryCardProps) => {
    const navigate = useNavigate()
    const { exportProject, exportBoltConnection } = useExportContext()
    const {
        archiveProject,
        archiveBoltConnection,
        duplicateStructure,
        restoreProject,
        restoreBoltConnection,
    } = useStructureContext()
    const { can } = useUserContext()
    const [qrVisible, setQrVisible] = useState(false)
    const [qr] = useQR(entry.referenceType, qrVisible ? entry.id : undefined)

    const canWriteStructure = can(PermissionSuffix.StructureWrite)

    const onClick = () => navigate(entry.referenceLink)

    const onExport = () => {
        if (entry.referenceType === StructureTypes.Project) return exportProject(entry.id)
        return exportBoltConnection(entry.id)
    }

    const onArchive = async () => {
        if (
            !confirm(`Are you sure you want to archive the ${entry.referenceType} "${entry.name}"?`)
        )
            return
        if (entry.referenceType === StructureTypes.Project) await archiveProject(entry.id)
        else await archiveBoltConnection(entry.id)
    }

    const onRestore = async () => {
        if (entry.referenceType === StructureTypes.Project) await restoreProject(entry.id)
        else await restoreBoltConnection(entry.id)
    }

    const onDuplicate = async () => {
        await duplicateStructure(entry)
    }

    const canDuplicate =
        !entry.isArchived &&
        (entry.referenceType === StructureTypes.BoltConnection || !entry.isRoot)

    const exportLabel =
        entry.referenceType === StructureTypes.Project
            ? t('project_export')
            : t('bolt_connection_export')

    const menu = new MenuBuilder()
        .addIf(
            getMenuItemViewQr(() => setQrVisible(true)),
            !entry.isArchived
        )
        .addIf(getMenuItemExport(onExport, exportLabel), !entry.isArchived)
        .addIf(getMenuItemDuplicate(onDuplicate), canWriteStructure && canDuplicate)
        .addIf(getMenuItemArchive(onArchive), canWriteStructure && !entry.isArchived)
        .addIf(getMenuItemRestore(onRestore), canWriteStructure && entry.isArchived)
        .toArray()

    const childCount = entry?.childCount ?? 0
    let summaryString = ''
    if (entry.referenceType === StructureTypes.Project) {
        if (childCount === 1) summaryString = `${childCount} ${t('common_subject')}`
        else summaryString = `${childCount} ${t('common_subjects')}`
    }

    const classNames = new ClassNameBuilder()
        .add('entry-card')
        .addIf('entry-card--archived', entry.isArchived)

    return (
        <>
            <Card
                className={classNames.toString()}
                onClick={onClick}
                title={
                    <Row className="entry-card__title" alignment="center" arrangement="spaced">
                        <Row
                            className="entry-card__title-main"
                            alignment="center"
                            arrangement="start"
                            gap={0.5}
                            grow
                        >
                            {(() => {
                                if (entry.isArchived) return <PrimeIcon name="file-o" />
                                switch (entry.referenceType) {
                                    case StructureTypes.Project:
                                        return <PrimeIcon name="folder" />
                                    case StructureTypes.BoltConnection:
                                        return <CustomIcon name="bolt" />
                                }
                            })()}
                            <div className="entry-card__title-text">{entry.name}</div>
                        </Row>
                        <CardContextMenu menu={menu} className="entry-card__menu" />
                    </Row>
                }
            >
                <Row arrangement="spaced" alignment="center">
                    {entry.referenceType === StructureTypes.Project ? (
                        <div className="entry-card__summary">{summaryString.toLowerCase()}</div>
                    ) : (
                        <div className="entry-card__summary">
                            {/* NOTE: Currently, hardware collections have an empty summary with a nbsp to keep the element's height. */}
                            &nbsp;
                        </div>
                    )}
                </Row>
            </Card>
            <QrDialog
                draggable={false}
                name={entry.name}
                qr={qr}
                visible={qrVisible}
                onHide={() => setQrVisible(false)}
            />
        </>
    )
}
